import { getComponent } from "../../../../client";
import { GroundMedia } from "../../../../modules/content/components/GroundMedia/GroundMedia";
import { GroundRichText } from "../../../../modules/content/components/GroundRichText/GroundRichText";
import type { IFooterBlockProps } from "./FooterBlock.props";

export const FooterBlock = ({
  contact,
  copyright,
  description,
  logo,
  navigationGroups,
  parentClass,
  secondaryLinks,
  socialLinks,
}: IFooterBlockProps) => {
  if (
    !logo &&
    !description &&
    !contact?.email &&
    !contact?.phone &&
    !contact?.address &&
    !navigationGroups?.length &&
    !socialLinks?.length &&
    !secondaryLinks?.length &&
    !copyright
  )
    return null;
  const Link = getComponent("Link");
  const linkClass =
    "inline-flex min-h-11 items-center font-ground-mono text-sm text-mineral-gray/80 transition-colors hover:text-mineral-gray focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-oxidized-orange md:text-xs";
  return (
    <footer className={`${parentClass || ""} bg-charcoal-green-ink text-mineral-gray`}>
      <div className="ground-container py-16">
        <div className="grid gap-12 border-t border-mineral-gray/35 pt-8 lg:grid-cols-[1.2fr_2fr_0.8fr]">
          {" "}
          <div>
            {logo ? (
              <GroundMedia
                className="mb-6 h-11 w-48 bg-transparent"
                imageClassName="object-contain object-left"
                media={logo}
                sizes="192px"
              />
            ) : null}
            {description ? (
              <GroundRichText
                className="max-w-sm text-base text-mineral-gray/80 md:text-lg"
                content={description}
              />
            ) : null}
            {contact ? (
              <address className="mt-6 flex flex-col border-t border-mineral-gray/25 pt-4 not-italic">
                {contact.email ? (
                  <a className={linkClass} href={`mailto:${contact.email}`}>
                    {contact.email}
                  </a>
                ) : null}
                {contact.phone ? (
                  <a className={linkClass} href={`tel:${contact.phone}`}>
                    {contact.phone}
                  </a>
                ) : null}
                {contact.address ? (
                  <p className="mt-2 max-w-[36ch] font-ground-mono text-sm leading-6 text-mineral-gray/80 md:text-xs">
                    {contact.address}
                  </p>
                ) : null}
              </address>
            ) : null}
          </div>
          {navigationGroups?.length ? (
            <nav aria-label="Footer" className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {navigationGroups.map((group, index) => (
                <div key={index}>
                  <h2 className="mb-3 font-ground-display text-sm font-bold">{group.header}</h2>
                  <ul>
                    {group.links.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        <Link
                          aria-label={link.accessibilityLabel || undefined}
                          className={linkClass}
                          href={link.href}
                          rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                          target={link.openInNewTab ? "_blank" : undefined}
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          ) : null}
          {socialLinks?.length ? (
            <div className="flex flex-wrap content-start gap-2">
              {socialLinks.map((item, index) => (
                <Link
                  aria-label={item.link.accessibilityLabel || undefined}
                  className="inline-flex min-h-11 items-center border border-mineral-gray/35 px-4 font-ground-mono text-sm hover:bg-mineral-gray hover:text-charcoal-green-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-oxidized-orange md:text-xs"
                  href={item.link.href}
                  key={index}
                  rel={item.link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={item.link.openInNewTab ? "_blank" : undefined}
                >
                  {item.link.label}
                </Link>
              ))}
            </div>
          ) : null}
        </div>
        <div className="mt-12 flex flex-col gap-3 border-t border-mineral-gray/35 pt-6 font-ground-mono text-sm text-mineral-gray/75 sm:flex-row sm:items-center sm:justify-between md:text-xs">
          {secondaryLinks?.length ? (
            <div className="flex flex-wrap gap-x-5">
              {secondaryLinks.map((link, index) => (
                <Link
                  aria-label={link.accessibilityLabel || undefined}
                  className={linkClass}
                  href={link.href}
                  key={index}
                  rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={link.openInNewTab ? "_blank" : undefined}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          ) : null}
          {copyright ? <p>{copyright}</p> : null}
        </div>
      </div>
    </footer>
  );
};

FooterBlock.displayName = "Block::FooterBlock";
